import React, { useState, useEffect } from 'react';
import { Game } from '../types';
import { GameCard } from '../components/game/GameCard';
import { Search, Gamepad2, ArrowUpDown } from 'lucide-react';

const ALPHABET = ['ALL', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

export const GamesDirectory: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [selectedLetter, setSelectedLetter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'popular' | 'name'>('popular');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const res = await fetch(`/api/games?sort=${sortBy}&search=${encodeURIComponent(search)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) setGames(data.games || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, [sortBy, search]);

  const filteredGames = games.filter((g) => {
    if (selectedLetter === 'ALL') return true;
    return g.name.toUpperCase().startsWith(selectedLetter);
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-brand-lightBorder dark:border-brand-darkBorder pb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-brand-blue/10 text-brand-blue dark:text-brand-accent mb-2">
            <Gamepad2 className="w-3.5 h-3.5" />
            <span>Games Index</span>
          </div>
          <h1 className="text-3xl font-black text-brand-lightText dark:text-brand-darkText">
            All Supported Games
          </h1>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Browse verified accounts, boosting services, items, and CD-keys across top multiplayer titles
          </p>
        </div>

        {/* Controls: Search & Sort */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search games..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl text-xs bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
            <Search className="w-4 h-4 text-brand-lightSecText absolute left-3 top-2.5" />
          </div>

          <div className="flex items-center gap-2 text-xs font-semibold">
            <button
              onClick={() => setSortBy('popular')}
              className={`px-3 py-2 rounded-xl transition-colors ${
                sortBy === 'popular'
                  ? 'bg-brand-blue text-white shadow-glow-blue'
                  : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText hover:text-brand-lightText dark:hover:text-white'
              }`}
            >
              Most Popular
            </button>
            <button
              onClick={() => setSortBy('name')}
              className={`px-3 py-2 rounded-xl transition-colors ${
                sortBy === 'name'
                  ? 'bg-brand-blue text-white shadow-glow-blue'
                  : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText hover:text-brand-lightText dark:hover:text-white'
              }`}
            >
              A - Z
            </button>
          </div>
        </div>
      </div>

      {/* Alphabetical Quick Jump Navigation */}
      <div className="flex flex-wrap items-center gap-1.5 p-2 rounded-2xl bg-brand-lightSec/80 dark:bg-brand-darkCard/80 border border-brand-lightBorder dark:border-brand-darkBorder overflow-x-auto">
        {ALPHABET.map((char) => (
          <button
            key={char}
            onClick={() => setSelectedLetter(char)}
            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center flex-shrink-0 ${
              selectedLetter === char
                ? 'bg-brand-blue text-white shadow-glow-blue'
                : 'text-brand-lightSecText hover:bg-white dark:hover:bg-brand-darkElevated hover:text-brand-lightText dark:hover:text-white'
            }`}
          >
            {char}
          </button>
        ))}
      </div>

      {/* Games Grid */}
      {filteredGames.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredGames.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 rounded-3xl bg-brand-lightSec dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder">
          <Gamepad2 className="w-12 h-12 text-brand-lightSecText mx-auto mb-3" />
          <h3 className="font-bold text-base text-brand-lightText dark:text-brand-darkText">No games found</h3>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">Try resetting your search query or letter filter</p>
        </div>
      )}
    </div>
  );
};
